import { useWorkspaceText } from '../interface-copy';
// The C2 presentation controls (ARCH-C2-PRESENTATION; layout: DES1-specs "C2 presentation
// controls layout"): spacing as a linked/unlinked pair per property, colour as one row per
// declared channel, and the effective default for an absent value.
//
// These are controls only. Which sides, channels and tokens exist, and each default, come from
// INS's manifest for the exact slot; the values they emit (a spacing token key or a px number,
// a colour token key) are validated and normalized by INS and applied by ART. Nothing here
// knows a palette, a CSS value or the stored schema.
import { useEffect, useId, useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy, type InspectCopyKey } from '@/i18n/ns/inspect';

export const SPACING_MAX = 256;
export const SPACING_STEP = 4;

export type SpacingProperty = 'margin' | 'padding' | 'gap';
export type SpacingSide = 'top' | 'right' | 'bottom' | 'left' | 'row' | 'column';
/** An allowed spacing token for the slot: its key, its validated px value and its short name. */
export type SpacingToken = { key: string; px: number; label: string };
/** One declared side of one spacing property. `value` is null when no override is stored. */
export type SpacingEntry = {
  key: string;
  side: SpacingSide;
  value: number | null;
  defaultPx: number | null;
  /** The selection disagrees on this side. */
  mixed?: boolean;
  /** INS declares a stored override here that may be cleared back to its default. */
  clearable?: boolean;
  /** A Reset is pending here: the default shows as what it will leave, not as stored. */
  clearing?: boolean;
};
export type SpacingGroup = { property: SpacingProperty; entries: SpacingEntry[] };
/** A token key or a literal px for INS to normalize (INS's PatchValue arms). */
export type PresentationValue = string | number;
/**
 * A new value for a slot, or Reset: a request to clear the stored override, which the sheet
 * sends as INS's `action: 'clear-override'` change. Never a null or a written default.
 */
export type PresentationChange =
  | { key: string; value: PresentationValue }
  | { key: string; clear: true };

export type ColourChannel = 'text' | 'background' | 'border' | 'fill' | 'stroke';
/**
 * ARCH-PRESENTATION-ABSENCE: a registered default of "no paint". It labels the default state
 * only; it is never offered, chosen or sent as a value.
 */
export const COLOUR_NONE = 'none';
export type ColourToken = { key: string; label: string };
export type ColourEntry = {
  key: string;
  channel: ColourChannel;
  value: string | null;
  defaultKey: string | null;
  mixed?: boolean;
  /** The colour-token keys INS's manifest declares for this slot and channel. */
  declared?: readonly string[];
  /** INS declares a stored override here that may be cleared back to its default. */
  clearable?: boolean;
  /** A Reset is pending here: the default shows as what it will leave, not as stored. */
  clearing?: boolean;
};

const PROPERTY_ORDER: SpacingProperty[] = ['margin', 'padding', 'gap'];
const SIDE_ORDER: SpacingSide[] = ['top', 'right', 'bottom', 'left', 'row', 'column'];
const CHANNEL_ORDER: ColourChannel[] = ['text', 'background', 'border', 'fill', 'stroke'];
const PROPERTY_COPY: Record<SpacingProperty, InspectCopyKey> = {
  margin: 'margin',
  padding: 'padding',
  gap: 'gap',
};
const SIDE_COPY: Record<SpacingSide, InspectCopyKey> = {
  top: 'sideTop',
  right: 'sideRight',
  bottom: 'sideBottom',
  left: 'sideLeft',
  row: 'rowGap',
  column: 'columnGap',
};
const CHANNEL_COPY: Record<ColourChannel, InspectCopyKey> = {
  text: 'channelText',
  background: 'channelBackground',
  border: 'channelBorder',
  fill: 'channelFill',
  stroke: 'channelStroke',
};

const bySide = (a: SpacingEntry, b: SpacingEntry) =>
  SIDE_ORDER.indexOf(a.side) - SIDE_ORDER.indexOf(b.side);
const effective = (e: SpacingEntry) => e.value ?? e.defaultPx;
const clamp = (n: number) => Math.min(SPACING_MAX, Math.max(0, Math.round(n)));

/** Linked shows one value only when every declared side is known and the same. */
export function linkedValue(entries: readonly SpacingEntry[]): {
  px: number | null;
  isDefault: boolean;
  mixed: boolean;
} {
  if (!entries.length || entries.some((e) => e.mixed))
    return { px: null, isDefault: false, mixed: true };
  const first = entries[0];
  const same = entries.every(
    (e) => effective(e) === effective(first) && (e.value === null) === (first.value === null),
  );
  if (!same) return { px: null, isDefault: false, mixed: true };
  return { px: effective(first), isDefault: first.value === null, mixed: false };
}

/** Margin, Padding and Gap rows for the slot's declared spacing, in that order. */
export function SpacingControls({
  groups,
  tokens,
  disabled = false,
  onChange,
}: {
  groups: readonly SpacingGroup[];
  tokens: readonly SpacingToken[];
  disabled?: boolean;
  onChange: (changes: PresentationChange[]) => void;
}) {
  const ordered = PROPERTY_ORDER.flatMap((p) =>
    groups.filter((g) => g.property === p && g.entries.length),
  );
  return (
    <div className="pc-inspect-pres" data-testid="inspect-spacing">
      {ordered.map((g) => (
        <SpacingRow
          key={g.property}
          group={g}
          tokens={tokens}
          disabled={disabled}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function SpacingRow({
  group,
  tokens,
  disabled,
  onChange,
}: {
  group: SpacingGroup;
  tokens: readonly SpacingToken[];
  disabled: boolean;
  onChange: (changes: PresentationChange[]) => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const entries = [...group.entries].sort(bySide);
  const shared = linkedValue(entries);
  // Linked by default; Mixed opens unlinked so each side shows what it is.
  const [linked, setLinked] = useState(!shared.mixed);
  const label =
    group.property === 'gap' && entries.length === 1
      ? copy[SIDE_COPY[entries[0].side]]
      : copy[PROPERTY_COPY[group.property]];
  const set = (targets: readonly SpacingEntry[], value: PresentationValue) =>
    onChange(targets.map((e) => ({ key: e.key, value })));
  const single = entries.length === 1;
  // Reset clears every stored override in the row that INS says may be cleared.
  const clearable = entries.filter((e) => e.clearable && e.value !== null);

  return (
    <div
      className="pc-inspect-pres-row"
      data-property={group.property}
      data-linked={single || linked}
    >
      <div className="pc-inspect-pres-head">
        <span className="pc-inspect-pres-label">{label}</span>
        <CurrentValue
          px={shared.px}
          mixed={shared.mixed}
          isDefault={shared.isDefault}
          pending={entries.every((e) => e.clearing)}
          unit={(n) => formatCopy(copy.pxValue, { n })}
        />
        {!single && (
          <button
            type="button"
            className="pc-inspect-link"
            aria-pressed={linked}
            aria-label={copy.sameOnAllSides}
            title={copy.sameOnAllSides}
            disabled={disabled}
            onClick={() => setLinked((v) => !v)}
          >
            <ChainIcon linked={linked} />
          </button>
        )}
        {clearable.length > 0 && (
          <button
            type="button"
            className="pc-btn pc-inspect-reset"
            data-variant="quiet"
            disabled={disabled}
            onClick={() => onChange(clearable.map((e) => ({ key: e.key, clear: true as const })))}
          >
            {copy.reset}
          </button>
        )}
      </div>
      {single || linked ? (
        <SpacingValue
          label={label}
          px={shared.mixed ? null : shared.px}
          tokens={tokens}
          disabled={disabled}
          onPick={(v) => set(entries, v)}
        />
      ) : (
        <div className="pc-inspect-pres-sides">
          {entries.map((e) => (
            <div key={e.key} className="pc-inspect-pres-side" data-side={e.side}>
              <span className="pc-inspect-pres-sublabel">
                {copy[SIDE_COPY[e.side]]}{' '}
                <CurrentValue
                  px={e.mixed ? null : effective(e)}
                  mixed={Boolean(e.mixed)}
                  isDefault={!e.mixed && e.value === null}
                  pending={e.clearing}
                  unit={(n) => formatCopy(copy.pxValue, { n })}
                />
              </span>
              <SpacingValue
                label={`${label}, ${copy[SIDE_COPY[e.side]]}`}
                px={e.mixed ? null : effective(e)}
                tokens={tokens}
                disabled={disabled}
                onPick={(v) => set([e], v)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CurrentValue({
  px,
  mixed,
  isDefault,
  pending = false,
  unit,
}: {
  px: number | null;
  mixed: boolean;
  isDefault: boolean;
  /** A pending Reset: the default is what it will leave (normal ink, not muted). */
  pending?: boolean;
  unit: (n: number) => string;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  if (mixed)
    return (
      <span className="pc-inspect-pres-current" data-kind="mixed">
        {copy.mixed}
      </span>
    );
  if (px === null) return null;
  return (
    <span
      className="pc-inspect-pres-current"
      data-kind={isDefault ? (pending ? 'pending-default' : 'default') : 'set'}
    >
      {isDefault ? formatCopy(copy.defaultValue, { value: unit(px) }) : unit(px)}
    </span>
  );
}

/** The slot's spacing tokens as a Segmented, then Custom… with a 0–256 px stepper. */
function SpacingValue({
  label,
  px,
  tokens,
  disabled,
  onPick,
}: {
  label: string;
  px: number | null;
  tokens: readonly SpacingToken[];
  disabled: boolean;
  onPick: (value: string | number) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(inspectCopy);
  const onToken = px !== null && tokens.some((t) => t.px === px);
  const [custom, setCustom] = useState(px !== null && !onToken);
  const [draft, setDraft] = useState(px === null ? '' : String(px));
  useEffect(() => setDraft(px === null ? '' : String(px)), [px]);
  const inputId = useId();
  const commit = (n: number) => {
    if (!Number.isFinite(n)) return;
    const v = clamp(n);
    setDraft(String(v));
    if (v !== px) onPick(v);
  };
  const base = px ?? 0;
  return (
    <div className="pc-inspect-pres-value">
      <div className="pc-inspect-seg" role="radiogroup" aria-label={label}>
        {tokens.map((t) => {
          const n = formatCopy(copy.pxValue, { n: t.px });
          return (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={!custom && px === t.px}
              aria-label={`${t.label}, ${n}`}
              title={n}
              data-token={t.key}
              disabled={disabled}
              onClick={() => {
                setCustom(false);
                if (px !== t.px) onPick(t.key);
              }}
            >
              {t.label}
            </button>
          );
        })}
        <button
          type="button"
          role="radio"
          aria-checked={custom}
          data-custom="true"
          disabled={disabled}
          onClick={() => setCustom(true)}
        >
          {copy.custom}
        </button>
      </div>
      {custom && (
        <div className="pc-inspect-stepper" data-testid="inspect-stepper">
          <button
            type="button"
            className="pc-btn"
            data-variant="secondary"
            aria-label={copy.smaller}
            disabled={disabled || base <= 0}
            onClick={() => commit(Math.ceil(base / SPACING_STEP) * SPACING_STEP - SPACING_STEP)}
          >
            −
          </button>
          <label className="sr-only" htmlFor={inputId}>
            {`${label}, ${copy.customPx}`}
          </label>
          <input
            id={inputId}
            className="pc-inspect-input"
            type="number"
            inputMode="numeric"
            min={0}
            max={SPACING_MAX}
            step={SPACING_STEP}
            value={draft}
            disabled={disabled}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => draft !== '' && commit(Number(draft))}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && draft !== '') commit(Number(draft));
            }}
          />
          <span className="pc-inspect-note" aria-hidden="true">
            {uiText('px')}
          </span>
          <button
            type="button"
            className="pc-btn"
            data-variant="secondary"
            aria-label={copy.larger}
            disabled={disabled || base >= SPACING_MAX}
            onClick={() => commit(Math.floor(base / SPACING_STEP) * SPACING_STEP + SPACING_STEP)}
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}

function ChainIcon({ linked }: { linked: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M6.5 9.5 9.5 6.5" opacity={linked ? 1 : 0} />
      <path d="M7 4.5 8.2 3.3a2.5 2.5 0 0 1 3.5 3.5L10.5 8" />
      <path d="M9 11.5 7.8 12.7a2.5 2.5 0 0 1-3.5-3.5L5.5 8" />
    </svg>
  );
}

/**
 * One row per declared colour channel. The swatch button opens the tokens `tokensFor` admits
 * for that row (DES1's keys for the channel ∩ the slot's declared keys; a popover, or in place
 * inside the sheet on phone). `swatch` resolves a key through DES1's table; a key it cannot
 * resolve is not offered.
 */
export function ColourControls({
  entries,
  tokensFor,
  swatch,
  phone = false,
  disabled = false,
  onChange,
}: {
  entries: readonly ColourEntry[];
  tokensFor: (entry: ColourEntry) => readonly ColourToken[];
  swatch: (key: string) => string | null;
  phone?: boolean;
  disabled?: boolean;
  onChange: (changes: PresentationChange[]) => void;
}) {
  const ordered = CHANNEL_ORDER.flatMap((c) => entries.filter((e) => e.channel === c));
  return (
    <div className="pc-inspect-pres" data-testid="inspect-colour">
      {ordered.map((e) => (
        <ColourRow
          key={e.key}
          entry={e}
          tokens={tokensFor(e).filter((t) => swatch(t.key) !== null)}
          swatch={swatch}
          phone={phone}
          disabled={disabled}
          onPick={(value) => onChange([{ key: e.key, value }])}
          onReset={() => onChange([{ key: e.key, clear: true }])}
        />
      ))}
    </div>
  );
}

function ColourRow({
  entry,
  tokens,
  swatch,
  phone,
  disabled,
  onPick,
  onReset,
}: {
  entry: ColourEntry;
  tokens: readonly ColourToken[];
  swatch: (key: string) => string | null;
  phone: boolean;
  disabled: boolean;
  onPick: (value: string) => void;
  onReset: () => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const [open, setOpen] = useState(false);
  // Phone has no hover tooltip: the focused swatch's name shows under the row.
  const [focused, setFocused] = useState<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const channel = copy[CHANNEL_COPY[entry.channel]];
  const shown = entry.mixed ? null : (entry.value ?? entry.defaultKey);
  const name = (key: string | null) =>
    key === COLOUR_NONE ? copy.colourNone : (tokens.find((t) => t.key === key)?.label ?? key ?? '');
  const paint = shown && shown !== COLOUR_NONE ? swatch(shown) : null;
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const label = formatCopy(copy.chooseColour, { channel: channel.toLocaleLowerCase() });
  const button = (
    <button
      ref={trigger}
      type="button"
      className="pc-inspect-swatch-button"
      disabled={disabled}
      {...(phone
        ? {
            'aria-haspopup': 'dialog' as const,
            'aria-expanded': open,
            'aria-controls': open ? panelId : undefined,
            onClick: () => setOpen((v) => !v),
          }
        : {})}
    >
      <span
        className="pc-inspect-swatch"
        aria-hidden="true"
        style={paint ? { background: paint } : undefined}
        data-empty={paint ? undefined : 'true'}
      />
      {entry.mixed ? (
        <span className="pc-inspect-pres-current" data-kind="mixed">
          {copy.mixed}
        </span>
      ) : entry.value === null ? (
        entry.defaultKey && (
          <span
            className="pc-inspect-pres-current"
            data-kind={entry.clearing ? 'pending-default' : 'default'}
          >
            {formatCopy(copy.defaultValue, { value: name(entry.defaultKey) })}
          </span>
        )
      ) : (
        <span className="pc-inspect-pres-current" data-kind="set">
          {name(entry.value)}
        </span>
      )}
    </button>
  );
  const head = (
    <div className="pc-inspect-pres-head">
      <span className="pc-inspect-pres-label">{channel}</span>
      {phone ? button : <Popover.Trigger asChild>{button}</Popover.Trigger>}
      {entry.clearable && entry.value !== null && !entry.mixed && (
        <button
          type="button"
          className="pc-btn pc-inspect-reset"
          data-variant="quiet"
          disabled={disabled}
          onClick={onReset}
        >
          {copy.reset}
        </button>
      )}
    </div>
  );
  const swatches = (
    <>
      <div className="pc-inspect-swatches" role="radiogroup" aria-label={channel}>
        {tokens.map((t) => {
          const now = !entry.mixed && (entry.value ?? entry.defaultKey) === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={now}
              aria-label={t.label}
              title={t.label}
              className="pc-inspect-swatch"
              data-token={t.key}
              style={{ background: swatch(t.key) ?? undefined }}
              onFocus={() => setFocused(t.label)}
              onClick={() => {
                if (entry.value !== t.key) onPick(t.key);
                close();
              }}
            >
              {now && (
                <span className="pc-inspect-swatch-check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
      {phone && (
        <p className="pc-inspect-swatch-name" aria-hidden="true" data-testid="inspect-swatch-name">
          {focused ?? name(entry.mixed ? null : (entry.value ?? entry.defaultKey))}
        </p>
      )}
    </>
  );
  // On phone the swatches open in place inside the inspector's sheet.
  if (phone)
    return (
      <div className="pc-inspect-pres-row" data-channel={entry.channel}>
        {head}
        {open && (
          <div
            id={panelId}
            className="pc-inspect-swatch-panel"
            data-mode="sheet"
            role="dialog"
            aria-label={label}
            onKeyDown={(event) => {
              // Escape closes the swatches first; the inspector's own Escape order waits.
              if (event.key !== 'Escape') return;
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation?.();
              close();
            }}
          >
            {swatches}
          </div>
        )}
      </div>
    );
  // Elsewhere a Radix popover (DESIGN-SPEC §6.9): Esc and a press outside close it. Focus starts
  // on the current swatch, inside the popover, so the inspector's Escape waits for it (layer.tsx).
  return (
    <div className="pc-inspect-pres-row" data-channel={entry.channel}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        {head}
        <Popover.Content
          ref={panel}
          className="pc-inspect-swatch-panel"
          data-mode="popover"
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          aria-label={label}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const radios = panel.current?.querySelectorAll<HTMLElement>('[role="radio"]');
            const current = panel.current?.querySelector<HTMLElement>('[aria-checked="true"]');
            (current ?? radios?.[0])?.focus({ preventScroll: true });
          }}
        >
          {swatches}
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
